import { doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { ouvirNumeros } from '@/services/conversasService';
import { PAPEIS_NUMERO } from '@/lib/constants';
import { tratarErroDeCallable } from '@/lib/erros';

/**
 * ★★★ WT lote 1 — TODA CALLABLE PASSA POR AQUI.
 *
 * ⚠⚠ Erro de callable chega ao cliente com o `code` do servidor (`permission-denied`,
 * `failed-precondition`, `resource-exhausted`) — e é **o diagnóstico mais valioso que
 * existe**: ele diz que a tela pediu algo que o servidor recusou. ★ Até agora ele só
 * aparecia na mensagem que a pessoa lia e esquecia.
 *
 * ★ O embrulho **registra e relança**: quem chama continua tratando o erro como antes
 * (as telas leem `e.code` para escolher a mensagem). ⚠ Engolir aqui quebraria todas elas.
 */
const chamarCallable = (nome) => async (dados) => {
  try {
    return await httpsCallable(functions, nome)(dados);
  } catch (erro) {
    throw tratarErroDeCallable(nome, erro);
  }
};


/**
 * Canais de WhatsApp da clínica (Configurações → Canais).
 *
 * A LEITURA da lista reaproveita o `ouvirNumeros` do conversasService: os
 * números já eram assinados pela Central de Conversas, e as rules já liberam
 * `read` para qualquer membro. Duplicar a assinatura só criaria duas fontes da
 * mesma verdade.
 *
 * A ESCRITA DE CANAL nunca é direta. Criar, conectar e consultar status passam
 * por callable porque a EVOLUTION_API_KEY autentica todas as operações da VPS —
 * no navegador ela ficaria legível no devtools de cada gestor. E escrever o
 * documento sem criar a instância deixaria um canal que existe no painel e não
 * existe na Evolution.
 *
 * `atualizarNumero` é a EXCEÇÃO, e é estreita: as mensagens automáticas
 * (saudação, ausência, encerramento) são texto puro, não tocam a VPS e não têm
 * contrapartida na Evolution. Não há instância a criar nem credencial a
 * proteger, então a callable só acrescentaria uma volta. As rules já restringem
 * `numeros` ao gestor.
 */

export { ouvirNumeros };

export const criarCanal = (dados) => chamarCallable('criarCanal')(dados);
export const conectarCanal = (dados) => chamarCallable('conectarCanal')(dados);
export const statusCanal = (dados) => chamarCallable('statusCanal')(dados);

/** Papéis que conectam por QR Code — os únicos que esta tela cadastra. */
export const PAPEIS_QR = Object.values(PAPEIS_NUMERO).filter((p) => p.conectaPorQr);

/**
 * Papéis ainda disponíveis para cadastro.
 *
 * Um papel por clínica: "o número de resgate desta clínica" é único, e o
 * servidor recusa o segundo com `already-exists`. Filtrar aqui evita oferecer
 * ao gestor uma opção que vai falhar.
 */
export function papeisDisponiveis(numeros = []) {
  const ocupados = new Set(numeros.map((n) => n.papel));
  return PAPEIS_QR.filter((p) => !ocupados.has(p.id));
}

/**
 * Sugere o nome da instância a partir do que a clínica já usa.
 *
 * Deriva o prefixo de um canal existente em vez de fixar "sorridents": a
 * clínica dois não se chama Sorridents, e um padrão fixo no código viraria
 * nome repetido — que o servidor recusa, porque o webhook resolve o tenant
 * pelo nome da instância e dois iguais entregariam a mensagem de uma clínica
 * no inbox da outra.
 *
 * `<prefixo>-recepcao` + papel resgate → `<prefixo>-resgate`.
 * Sem canal nenhum ainda, devolve '' e o gestor digita.
 */
export function sugerirInstancia(numeros = [], papelId) {
  const sufixo = PAPEIS_NUMERO[papelId]?.sufixoInstancia;
  if (!sufixo) return '';

  const existente = numeros.map((n) => n.evolution?.instancia).find(Boolean);
  if (!existente) return '';

  // Só o último trecho é o papel; o resto é o prefixo da clínica.
  const prefixo = existente.includes('-')
    ? existente.slice(0, existente.lastIndexOf('-'))
    : existente;
  return prefixo ? `${prefixo}-${sufixo}` : '';
}

/** Traduz os códigos das callables para algo que o gestor entenda. */
export const MENSAGENS_CANAL = {
  'functions/permission-denied': 'Apenas o gestor da clínica pode administrar canais.',
  'functions/unauthenticated': 'Sua sessão expirou. Entre de novo.',
  'functions/already-exists':
    'Este canal já existe, ou o nome de instância já está em uso. Escolha outro nome.',
  'functions/invalid-argument': 'Confira os dados: papel, telefone (E.164) e nome da instância.',
  // ★★★ A-8.1: estas TRÊS mensagens citavam "Evolution" e NÃO estavam na lista dos 11 —
  // ⚠⚠ o grep original varreu `components`, `pages` e `lib`, e esqueceu `services`.
  // ★ Quem as achou foi a guarda de varredura (`lib/a81.vocabulario.guardas.test.js`),
  // que é exatamente o que ela existe para fazer.
  'functions/not-found': 'Este canal não existe mais no servidor de mensagens. Cadastre-o de novo.',
  'functions/failed-precondition':
    'O servidor de mensagens recusou a operação. Confira se ele está no ar e tente de novo.',
  'functions/deadline-exceeded': 'O servidor de mensagens demorou demais para responder. Tente de novo.',
  'functions/unavailable':
    'Não foi possível falar com o servidor. Confira a conexão e tente de novo.',
};

export const mensagemDoErro = (e, padrao = 'Não foi possível concluir a operação.') =>
  MENSAGENS_CANAL[e?.code] ?? e?.message ?? padrao;

/**
 * Atualiza campos do documento do número.
 *
 * `updateDoc` com os campos informados, e NÃO `setDoc`: o documento guarda
 * `evolution.instancia`, `provider`, `telefone` e o `papel`, e sobrescrevê-lo
 * para trocar uma saudação apagaria a ligação com a instância da VPS — o
 * webhook descobre a clínica justamente por `evolution.instancia`.
 *
 * Use só para os campos de texto das mensagens automáticas. Conexão e provider
 * continuam pelas callables acima.
 */
export async function atualizarNumero(clinicaId, numeroId, campos) {
  await updateDoc(doc(db, 'clinicas', clinicaId, 'numeros', numeroId), campos);
}
